import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import {
  Webhook,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  History,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Loader2,
  Trash2
} from 'lucide-react';

const PROVIDER_INFO = {
  SHIPROCKET: {
    name: 'Shiprocket',
    color: 'bg-blue-500',
    icon: '🚚',
    docUrl: 'https://apidocs.shiprocket.in/#tag/Webhooks'
  },
  DELHIVERY: {
    name: 'Delhivery',
    color: 'bg-red-500',
    icon: '📦',
    docUrl: 'https://developers.delhivery.com/docs/webhooks'
  },
  RAZORPAY: {
    name: 'Razorpay',
    color: 'bg-indigo-500',
    icon: '💳',
    docUrl: 'https://razorpay.com/docs/webhooks/'
  },
  ZOHO: {
    name: 'Zoho',
    color: 'bg-yellow-500',
    icon: '📊',
    docUrl: 'https://www.zoho.com/invoice/api'
  },
  SHOPIFY: {
    name: 'Shopify',
    color: 'bg-green-500',
    icon: '🛒',
    docUrl: 'https://shopify.dev/docs/apps/webhooks'
  },
  MSG91: {
    name: 'MSG91',
    color: 'bg-purple-500',
    icon: '📱',
    docUrl: 'https://docs.msg91.com/'
  }
};

const WebhookManagerPage = () => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [regeneratingProvider, setRegeneratingProvider] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Canonical production URL for Shopify webhook registration.
  // Shopify webhooks MUST point to the production domain (seller.aniyor.net) —
  // never to preview.emergentagent.com or geo-load-balanced deploy URLs.
  // Editable below so admin can override during staging/testing.
  const PRODUCTION_WEBHOOK_BASE_URL = 'https://seller.aniyor.net/api';
  const [webhookTargetUrl, setWebhookTargetUrl] = useState(PRODUCTION_WEBHOOK_BASE_URL);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/webhook-manager/configs');
      setConfigs(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load webhook configurations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`
    });
  };

  const revealSecret = async (provider) => {
    try {
      const data = await apiCall(`/api/webhook-manager/configs/${provider}/reveal-secret`, {
        method: 'POST'
      });
      setRevealedSecrets(prev => ({ ...prev, [provider]: data.secret_token }));
      toast({
        title: 'Secret Revealed',
        description: 'The secret token is now visible'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reveal secret',
        variant: 'destructive'
      });
    }
  };

  const hideSecret = (provider) => {
    setRevealedSecrets(prev => {
      const newState = { ...prev };
      delete newState[provider];
      return newState;
    });
  };

  const handleRegenerateSecret = async () => {
    if (!regeneratingProvider) return;
    
    try {
      const data = await apiCall(`/api/webhook-manager/configs/${regeneratingProvider}/regenerate-secret`, {
        method: 'POST'
      });
      
      // Update the config in state
      setConfigs(prev => prev.map(c => 
        c.provider === regeneratingProvider 
          ? { ...c, secret_token_masked: '****' + data.secret_token.slice(-4) }
          : c
      ));
      
      // Show the new secret
      setRevealedSecrets(prev => ({ ...prev, [regeneratingProvider]: data.secret_token }));
      
      toast({
        title: 'Secret Regenerated',
        description: 'Update this new secret in your 3rd party dashboard',
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to regenerate secret',
        variant: 'destructive'
      });
    } finally {
      setRegenerateConfirmOpen(false);
      setRegeneratingProvider(null);
    }
  };

  const toggleWebhook = async (provider, isActive) => {
    try {
      await apiCall(`/api/webhook-manager/configs/${provider}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: isActive })
      });
      
      setConfigs(prev => prev.map(c => 
        c.provider === provider ? { ...c, is_active: isActive } : c
      ));
      
      toast({
        title: isActive ? 'Webhook Enabled' : 'Webhook Disabled',
        description: `${provider} webhook is now ${isActive ? 'active' : 'inactive'}`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update webhook status',
        variant: 'destructive'
      });
    }
  };

  const viewLogs = async (provider) => {
    setSelectedProvider(provider);
    setLogsModalOpen(true);
    setLogsLoading(true);
    
    try {
      const data = await apiCall(`/api/webhook-manager/configs/${provider}/logs?limit=10`);
      setLogsData(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load webhook logs',
        variant: 'destructive'
      });
      setLogsData([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const seedConfigs = async () => {
    try {
      setSeeding(true);
      const result = await apiCall('/api/webhook-manager/seed-configs', {
        method: 'POST'
      });
      
      toast({
        title: 'Webhook Configs Initialized',
        description: `Created ${result.created?.length || 0} configurations`
      });
      
      // Refresh the list
      await fetchConfigs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize webhook configurations',
        variant: 'destructive'
      });
    } finally {
      setSeeding(false);
    }
  };

  const healEndpoints = async () => {
    try {
      const result = await apiCall('/api/webhook-manager/heal-endpoints', { method: 'POST' });
      toast({
        title: result.patched > 0 ? `Fixed ${result.patched} URL(s)` : 'All URLs already correct',
        description: result.message,
      });
      await fetchConfigs();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to heal endpoints', variant: 'destructive' });
    }
  };

  // Shopify webhook auto-registration
  const [shopifyWebhookStatus, setShopifyWebhookStatus] = useState(null);
  const [registeringWebhooks, setRegisteringWebhooks] = useState(false);

  // Read-only webhook diagnostics — never mutates Shopify subscriptions.
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagStores, setDiagStores] = useState(null);
  const [testingTopic, setTestingTopic] = useState(null);   // "shop::topic" while a test is in-flight
  const [topicTestResults, setTopicTestResults] = useState({}); // { "shop::topic": {ok, status_code, ...} }

  const loadLiveWebhooks = async () => {
    setDiagLoading(true);
    try {
      const data = await apiCall('/api/shopify-setup/live-webhooks');
      setDiagStores(data.stores || []);
    } catch (error) {
      toast({
        title: 'Failed to load live webhooks',
        description: error.message || String(error),
        variant: 'destructive'
      });
    } finally {
      setDiagLoading(false);
    }
  };

  const runTopicTest = async (shop_url, topic) => {
    const key = `${shop_url}::${topic}`;
    setTestingTopic(key);
    try {
      const res = await apiCall('/api/shopify-setup/test-webhook-topic', {
        method: 'POST',
        body: JSON.stringify({ shop_url, topic })
      });
      setTopicTestResults(prev => ({ ...prev, [key]: res }));
    } catch (error) {
      setTopicTestResults(prev => ({
        ...prev,
        [key]: { ok: false, status_code: null, error: error.message || String(error) }
      }));
    } finally {
      setTestingTopic(null);
    }
  };

  const fetchShopifyWebhookStatus = async () => {
    try {
      const data = await apiCall('/api/shopify-setup/webhook-status');
      setShopifyWebhookStatus(data);
    } catch (error) {
      // Silently fail — not critical
    }
  };

  useEffect(() => {
    fetchShopifyWebhookStatus();
  }, []);

  const handleRegisterShopifyWebhooks = async () => {
    setRegisteringWebhooks(true);
    try {
      const result = await apiCall('/api/shopify-setup/auto-register-webhooks', {
        method: 'POST',
        body: JSON.stringify({ webhook_base_url: webhookTargetUrl })
      });
      toast({
        title: 'Shopify Webhooks Registered',
        description: result.message,
      });
      fetchShopifyWebhookStatus();
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.message || 'Failed to register Shopify webhooks',
        variant: 'destructive'
      });
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  const handleCleanupAndReregister = async () => {
    if (!window.confirm(
      `This will DELETE all existing Shopify webhooks across every connected store, then re-register the full canonical 21-topic list pointing to:\n\n  ${webhookTargetUrl}\n\nUse this after a deploy, URL change, or when webhooks are failing HMAC validation.\n\nContinue?`
    )) return;
    setRegisteringWebhooks(true);
    try {
      const result = await apiCall('/api/shopify-setup/cleanup-and-reregister-webhooks', {
        method: 'POST',
        body: JSON.stringify({ webhook_base_url: webhookTargetUrl })
      });
      const stores = (result.results || []).length;
      const totalCreated = (result.results || []).reduce((n, r) => n + (r.created?.length || 0), 0);
      const totalDeleted = (result.results || []).reduce((n, r) => n + (r.deleted?.length || 0), 0);
      toast({
        title: 'Cleanup & Re-register Complete',
        description: `${stores} store(s): ${totalDeleted} old webhooks deleted, ${totalCreated} new ones registered.`,
      });
      fetchShopifyWebhookStatus();
    } catch (error) {
      toast({
        title: 'Cleanup Failed',
        description: error.message || 'Failed to cleanup & re-register webhooks',
        variant: 'destructive'
      });
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  const handleReinstallScriptTag = async () => {
    if (!window.confirm(
      'This will DELETE all Shopify script tags on every connected store, then re-register a single fresh script tag pointing at the current storefront.js version.\n\nUse this after a deploy when the review widget / marketplace JS changes are not reaching customers (URL cache-bust).\n\nContinue?'
    )) return;
    setRegisteringWebhooks(true);
    try {
      const result = await apiCall('/api/shopify-app/reinstall-script-tag', { method: 'POST' });
      const ok = (result.results || []).filter(r => r.status === 'reinstalled').length;
      const failed = (result.results || []).filter(r => r.status === 'failed').length;
      toast({
        title: 'Script tag re-installed',
        description: `${ok} store(s) updated${failed ? ` — ${failed} failed` : ''}. Customers will fetch the fresh JS on next PDP load.`,
      });
    } catch (error) {
      toast({
        title: 'Reinstall Failed',
        description: error.message || 'Failed to reinstall script tag',
        variant: 'destructive'
      });
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  // iter-222 — Buy Guard storefront embed UI removed (was deprecated;
  // unified storefront script now owns all PDP/cart logic).

  const getStatusBadge = (config) => {
    if (!config.last_event_at) {
      return <Badge variant="outline" className="text-gray-500">No Events</Badge>;
    }
    
    if (config.last_http_status === 200) {
      return <Badge className="bg-green-100 text-green-700">✓ Healthy</Badge>;
    }
    
    // Show specific error status
    const statusMessages = {
      401: 'Auth Failed - Check secret token',
      403: 'Forbidden - Invalid signature',
      400: 'Bad Request - Invalid payload',
      500: 'Server Error'
    };
    const message = statusMessages[config.last_http_status] || `HTTP ${config.last_http_status}`;
    
    return (
      <Badge className="bg-red-100 text-red-700" title={message}>
        ⚠ {message}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500">You need admin privileges to access this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Webhook className="h-6 w-6" />
              Webhook Manager
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage incoming webhooks from 3rd party services
            </p>
          </div>
          <Button onClick={fetchConfigs} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-600">{configs.length}</div>
            <div className="text-sm text-gray-500">Total Webhooks</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {configs.filter(c => c.is_active).length}
            </div>
            <div className="text-sm text-gray-500">Active</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              {configs.reduce((sum, c) => sum + c.total_events_received, 0)}
            </div>
            <div className="text-sm text-gray-500">Total Events</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-red-600">
              {configs.reduce((sum, c) => sum + c.total_events_failed, 0)}
            </div>
            <div className="text-sm text-gray-500">Failed Events</div>
          </Card>
        </div>

        {/* Shopify Order Auto-Sync (Webhook Registration) */}
        <Card className="mb-6 border-green-200 bg-green-50/30" data-testid="shopify-webhook-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="w-6 h-6 bg-green-500 rounded text-white flex items-center justify-center text-xs font-bold">S</span>
              Shopify Order Auto-Sync
            </CardTitle>
            <CardDescription>
              Register webhooks on your master Shopify store to auto-sync orders as they arrive.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Target webhook URL — canonical production domain */}
            <div className="mb-3 p-3 bg-white border border-green-200 rounded-md">
              <label className="text-xs font-medium text-gray-600 mb-1 block flex items-center justify-between">
                <span>Webhook Target URL <span className="text-red-500">*</span></span>
                {webhookTargetUrl !== PRODUCTION_WEBHOOK_BASE_URL && (
                  <button
                    type="button"
                    onClick={() => setWebhookTargetUrl(PRODUCTION_WEBHOOK_BASE_URL)}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="reset-webhook-url-btn"
                  >
                    Reset to production
                  </button>
                )}
              </label>
              <Input
                value={webhookTargetUrl}
                onChange={(e) => setWebhookTargetUrl(e.target.value.trim())}
                className="font-mono text-sm"
                placeholder="https://seller.aniyor.net/api"
                data-testid="webhook-target-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Only used when you explicitly click "Register" or "Cleanup & Re-register" below. Existing Shopify subscriptions are not touched.
              </p>
              {webhookTargetUrl.includes('preview.emergentagent.com') && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  ⚠ Preview URL detected. Shopify will not deliver webhooks reliably to preview hosts. Reset to production.
                </p>
              )}
            </div>
            {shopifyWebhookStatus ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-sm">
                    <span className="text-gray-500">Stores:</span>{' '}
                    <span className="font-medium">{shopifyWebhookStatus.total_stores}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Registered:</span>{' '}
                    <span className="font-medium text-green-600">{shopifyWebhookStatus.registered}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Pending:</span>{' '}
                    <span className={`font-medium ${shopifyWebhookStatus.pending > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {shopifyWebhookStatus.pending}
                    </span>
                  </div>
                  {shopifyWebhookStatus.stores?.map((store, i) => (
                    <Badge key={i} variant={store.webhook_registered ? 'default' : 'outline'} className="text-xs">
                      {store.shop_url?.replace('.myshopify.com', '')} — {store.webhook_registered ? 'Active' : 'Pending'}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRegisterShopifyWebhooks}
                    disabled={registeringWebhooks || shopifyWebhookStatus.pending === 0 || !webhookTargetUrl.startsWith('https://') || webhookTargetUrl.includes('preview.emergentagent.com')}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="register-shopify-webhooks-btn"
                  >
                    {registeringWebhooks ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Registering...</>
                    ) : shopifyWebhookStatus.pending === 0 ? (
                      <><CheckCircle className="h-4 w-4 mr-2" /> All Registered</>
                    ) : (
                      <><RefreshCw className="h-4 w-4 mr-2" /> Register Webhooks</>
                    )}
                  </Button>
                  <Button
                    onClick={handleCleanupAndReregister}
                    disabled={registeringWebhooks || !webhookTargetUrl.startsWith('https://') || webhookTargetUrl.includes('preview.emergentagent.com')}
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="cleanup-reregister-webhooks-btn"
                    title="Deletes ALL stale webhooks and re-registers the full canonical list. Use after deploys / URL changes."
                  >
                    {registeringWebhooks ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Working...</>
                    ) : (
                      <><RefreshCw className="h-4 w-4 mr-2" /> Cleanup &amp; Re-register</>
                    )}
                  </Button>
                  <Button
                    onClick={handleReinstallScriptTag}
                    disabled={registeringWebhooks}
                    size="sm"
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="reinstall-script-tag-btn"
                    title="Re-installs the Shopify script_tag with a fresh URL — forces customers to fetch new storefront.js on next PDP load."
                  >
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh Storefront JS
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Loading Shopify store status...</div>
            )}

            {/* Read-only diagnostic panel — lists live subscribed webhooks and lets you fire a signed sample payload per topic. */}
            <div className="mt-4 border-t border-green-200 pt-3">
              <button
                type="button"
                onClick={() => {
                  const next = !diagOpen;
                  setDiagOpen(next);
                  if (next && !diagStores) loadLiveWebhooks();
                }}
                className="text-sm text-green-800 font-medium hover:underline flex items-center gap-2"
                data-testid="toggle-webhook-diagnose-btn"
              >
                {diagOpen ? '▾' : '▸'} Diagnose Webhooks (read-only)
              </button>
              {diagOpen && (
                <div className="mt-3 space-y-4">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={loadLiveWebhooks}
                      disabled={diagLoading}
                      data-testid="refresh-live-webhooks-btn"
                    >
                      {diagLoading ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Loading...</>
                      ) : (
                        <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reload from Shopify</>
                      )}
                    </Button>
                    <span className="text-xs text-gray-500">Fetches the actual /webhooks.json list from Shopify. Never modifies subscriptions.</span>
                  </div>

                  {diagStores && diagStores.length === 0 && (
                    <div className="text-sm text-gray-500">No active Shopify stores found.</div>
                  )}

                  {diagStores && diagStores.map((store, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">
                          {store.shop_url}{' '}
                          {store.count != null && (
                            <span className="text-xs text-gray-500">— {store.count} webhook(s) subscribed</span>
                          )}
                        </div>
                      </div>
                      {store.error && (
                        <div className="text-xs text-red-600 mb-2">Error reading webhooks: {store.error}</div>
                      )}
                      {store.webhooks && store.webhooks.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs" data-testid={`live-webhooks-table-${idx}`}>
                            <thead className="text-gray-500 border-b">
                              <tr>
                                <th className="text-left py-1.5 pr-3">Topic</th>
                                <th className="text-left py-1.5 pr-3">Address</th>
                                <th className="text-left py-1.5 pr-3">Prod?</th>
                                <th className="text-left py-1.5 pr-3">Test</th>
                                <th className="text-left py-1.5 pr-3">Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {store.webhooks.map((wh, whIdx) => {
                                const key = `${store.shop_url}::${wh.topic}`;
                                const result = topicTestResults[key];
                                const isTesting = testingTopic === key;
                                return (
                                  <tr key={whIdx} className="border-b last:border-0">
                                    <td className="py-1.5 pr-3 font-mono">{wh.topic}</td>
                                    <td className="py-1.5 pr-3 font-mono text-gray-600 truncate max-w-[420px]" title={wh.address}>
                                      {wh.address}
                                    </td>
                                    <td className="py-1.5 pr-3">
                                      {wh.points_to_production ? (
                                        <span className="text-green-600">✓</span>
                                      ) : (
                                        <span className="text-amber-600" title="Address does not resolve to seller.aniyor.net">⚠</span>
                                      )}
                                    </td>
                                    <td className="py-1.5 pr-3">
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:underline disabled:opacity-50"
                                        onClick={() => runTopicTest(store.shop_url, wh.topic)}
                                        disabled={isTesting}
                                        data-testid={`test-topic-btn-${wh.topic.replace('/', '-')}`}
                                      >
                                        {isTesting ? 'Testing…' : 'Send sample'}
                                      </button>
                                    </td>
                                    <td className="py-1.5 pr-3">
                                      {result && (
                                        <span
                                          className={result.ok ? 'text-green-600' : 'text-red-600'}
                                          title={result.error || result.response_snippet || ''}
                                        >
                                          {result.ok
                                            ? `${result.status_code} OK`
                                            : `${result.status_code || 'ERR'}${result.error ? ' — ' + result.error.slice(0, 60) : ''}`}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* iter-222 — Aniyor Buy Guard storefront embed REMOVED.
            The unified storefront script (shopify_theme.py) now owns
            all PDP/cart logic. Seller-portal preview lives on the
            ProductDetails page (BuyGuardPreview.jsx). */}

        {/* Webhook Cards — Fix URLs banner shown when any config has a stale endpoint */}
        {configs.length > 0 && configs.some(c => !c.full_endpoint?.includes('/webhook-manager/incoming/')) && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-800">
              <strong>Stale webhook URLs detected.</strong> Some Callback URLs still point to the old <code>/api/webhooks/</code> path. Click "Fix URLs" to update them to the canonical path.
            </p>
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 ml-4 shrink-0" onClick={healEndpoints} data-testid="heal-webhook-urls-btn">
              Fix URLs
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : configs.length === 0 ? (
          <Card className="p-8 text-center">
            <Webhook className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Webhook Configurations</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Webhook configurations haven't been initialized yet. Click below to set up default configurations for all supported providers.
            </p>
            {(user?.role === 'super_admin' || user?.role === 'admin' || user?.admin_type === 'super_admin') ? (
              <Button 
                onClick={seedConfigs} 
                disabled={seeding}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {seeding ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <Webhook className="h-4 w-4 mr-2" />
                    Initialize Webhook Configs
                  </>
                )}
              </Button>
            ) : (
              <p className="text-sm text-amber-600">
                Please ask an Admin to initialize webhook configurations.
              </p>
            )}
          </Card>
        ) : (
          <div className="grid gap-4">
            {configs.map((config) => {
              const providerInfo = PROVIDER_INFO[config.provider] || { 
                name: config.provider, 
                color: 'bg-gray-500', 
                icon: '🔗' 
              };
              const fullUrl = `${baseUrl}${config.full_endpoint}`;
              const isRevealed = !!revealedSecrets[config.provider];
              
              return (
                <Card key={config.id} className={`${!config.is_active ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${providerInfo.color} flex items-center justify-center text-white text-lg`}>
                          {providerInfo.icon}
                        </div>
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {providerInfo.name}
                            {getStatusBadge(config)}
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {config.description}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={config.is_active}
                          onCheckedChange={(checked) => toggleWebhook(config.provider, checked)}
                        />
                        {providerInfo.docUrl && (
                          <a 
                            href={providerInfo.docUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Callback URL */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Callback URL</label>
                      <div className="flex gap-2">
                        <Input 
                          value={fullUrl} 
                          readOnly 
                          className="font-mono text-sm bg-gray-50"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(fullUrl, 'Callback URL')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Secret Token */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">
                        Secret Token 
                        <span className="text-gray-400 ml-1">({config.verification_type})</span>
                      </label>
                      <div className="flex gap-2">
                        <Input 
                          value={isRevealed ? revealedSecrets[config.provider] : config.secret_token_masked}
                          readOnly 
                          type={isRevealed ? "text" : "password"}
                          className="font-mono text-sm bg-gray-50"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => isRevealed ? hideSecret(config.provider) : revealSecret(config.provider)}
                        >
                          {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        {isRevealed && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => copyToClipboard(revealedSecrets[config.provider], 'Secret Token')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Stats & Actions */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          {config.total_events_verified} verified
                        </span>
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3 w-3 text-red-500" />
                          {config.total_events_failed} failed
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last: {formatDate(config.last_event_at)}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => viewLogs(config.provider)}
                        >
                          <History className="h-4 w-4 mr-1" />
                          View Logs
                        </Button>
                        {(user?.role === 'super_admin' || user?.role === 'admin' || user?.admin_type === 'super_admin') && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-orange-600 hover:text-orange-700"
                            onClick={() => {
                              setRegeneratingProvider(config.provider);
                              setRegenerateConfirmOpen(true);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Regenerate
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Logs Modal */}
        <Dialog open={logsModalOpen} onOpenChange={setLogsModalOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Recent Webhook Events - {selectedProvider}
              </DialogTitle>
              <DialogDescription>
                Last 10 webhook requests received
              </DialogDescription>
            </DialogHeader>
            
            {logsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : logsData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No webhook events logged yet
              </div>
            ) : (
              <div className="space-y-3">
                {logsData.map((log) => (
                  <Card key={log.id} className={`p-3 ${log.verified ? 'border-green-200' : 'border-red-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {log.verified ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="font-mono text-sm">{log.http_method}</span>
                        <Badge variant={log.http_status === 200 ? 'success' : 'destructive'}>
                          {log.http_status}
                        </Badge>
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(log.received_at)}</span>
                    </div>
                    
                    {log.error_message && (
                      <div className="text-sm text-red-600 bg-red-50 p-2 rounded mb-2">
                        {log.error_message}
                      </div>
                    )}
                    
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                        View Payload
                      </summary>
                      <pre className="mt-2 p-2 bg-gray-50 rounded overflow-x-auto text-xs">
                        {JSON.stringify(log.request_body, null, 2)}
                      </pre>
                    </details>
                  </Card>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Regenerate Confirmation Dialog */}
        <AlertDialog open={regenerateConfirmOpen} onOpenChange={setRegenerateConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Regenerate Secret Token?
              </AlertDialogTitle>
              <AlertDialogDescription>
                <p className="mb-2">
                  This will invalidate the current secret token for <strong>{regeneratingProvider}</strong>.
                </p>
                <p className="text-orange-600 font-medium">
                  ⚠️ The webhook will stop working until you update the new secret in the 3rd party dashboard.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleRegenerateSecret}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Regenerate Token
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default WebhookManagerPage;
